import React from 'react';

import { useConfiguration } from '../../context/configurationContext';
import { LinkedLogo, NamedLink, ExternalLink } from '../../components';

/** Extract `[label](href)` markdown links from the Console footer blocks. */
const parseFooterLinks = footer => {
  const blocks = Array.isArray(footer?.blocks) ? footer.blocks : [];
  const links = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  blocks.forEach(b => {
    const content = b?.text?.content || '';
    let m;
    while ((m = re.exec(content)) !== null) {
      links.push({ label: m[1].trim(), href: m[2].trim() });
    }
  });
  return links;
};

/** Map a footer href to NamedLink route props so links render exactly like the hardcoded ones. */
const toNamedLinkProps = href => {
  if (href === '/terms-of-service') return { name: 'TermsOfServicePage' };
  if (href === '/privacy-policy') return { name: 'PrivacyPolicyPage' };
  const cms = href.match(/^\/?p\/(.+)$/);
  if (cms) return { name: 'CMSPage', params: { pageId: cms[1] } };
  return null;
};

/**
 * CustomFooter: responsive footer matching Replit design.
 * Mobile: stacked center-aligned. Tablet+: logo left, links right.
 * Links come from the Console `footer.json` asset.
 */
const CustomFooter = () => {
  const config = useConfiguration();
  const links = parseFooterLinks(config?.footer);
  const slogan = config?.footer?.slogan?.content?.trim();

  return (
    <footer className="bg-secondary text-secondary-foreground py-10 mt-auto">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          {/* Brand */}
          <div className="flex items-center gap-2 shrink-0">
            <LinkedLogo
              rootClassName="flex items-center flex-shrink-0"
              logoClassName="flex items-center"
              logoImageClassName="h-12 w-12 object-contain"
              layout="desktop"
              alt="ConstructionYardSale.com"
            />
            <NamedLink name="LandingPage" className="flex items-center no-underline">
              <span className="font-heading font-bold text-xl uppercase tracking-wider text-white">
                Construction<span className="text-primary">YardSale</span>.com
              </span>
            </NamedLink>
          </div>

          {/* Links: 2-column grid on mobile, single row on md+ */}
          <nav className="grid grid-cols-2 gap-x-8 gap-y-3 text-center md:flex md:flex-wrap md:gap-6 text-sm font-heading font-medium tracking-wide">
            {links.map((l, i) => {
              if (/^https?:\/\//i.test(l.href)) {
                return (
                  <ExternalLink
                    key={`${l.href}-${i}`}
                    href={l.href}
                    className="hover:text-primary transition-colors"
                  >
                    {l.label}
                  </ExternalLink>
                );
              }
              const linkProps = toNamedLinkProps(l.href);
              if (!linkProps) {
                return null;
              }
              return (
                <NamedLink key={`${l.href}-${i}`} {...linkProps} className="hover:text-primary transition-colors">
                  {l.label}
                </NamedLink>
              );
            })}
          </nav>
        </div>

        {slogan ? (
          <p className="mt-6 text-center text-sm text-white/80">{slogan}</p>
        ) : null}

        <div className="mt-8 text-center text-xs text-secondary-foreground/50">
          &copy; {new Date().getFullYear()} ConstructionYardSale.com. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default CustomFooter;
